import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildVehiclePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { buildMpgStylesPath, MpgEntities, MpgModel } from 'client/data/models/mpg';
import { MpgParams } from 'site-modules/shared/utils/mpg/params';
import { getQuery } from 'client/utils/location';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { AlsoViewedVehiclesCrossShop } from 'site-modules/shared/components/core-page/also-viewed-vehicles-cross-shop/also-viewed-vehicles-cross-shop';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { DifferentVehicleMpg } from 'site-modules/mpg/components/different-vehicle-mpg/different-vehicle-mpg';
import {
  CHANGE_VEHICLE_ID,
  ELECTRIC_PAGE_DISPLAY_NAME,
  PAGE_DISPLAY_NAME,
  URL_PATH,
} from 'site-modules/shared/constants/mpg';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/mpg/mpg';
import { EngineTypeQuizMap } from 'client/tracking/maps/common/engine-type-quiz';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { PricingCarouselFooter } from 'site-modules/shared/components/core-page/pricing-carousel/pricing-carousel-footer';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { FuelEconomyData } from 'site-modules/shared/components/mpg/fuel-economy-data/fuel-economy-data';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import classnames from 'classnames';

function MpgUI({ vehicle, params, styles, location, isMobile }) {
  const styleId = getQuery(location).style || get(styles, '[0].id');

  useEffect(() => {
    TrackingHandler.useMap({ ...TrackingMap, ...EngineTypeQuizMap });
  }, []);

  if (!vehicle || !styles) {
    return (
      <main id="main-content" className="mpg">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const {
    make: { slug: makeSlug },
    model: { slug: modelSlug, modelLinkCode },
  } = vehicle;
  const currentStyle = styles.find(({ id }) => id === styleId);
  const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear: vehicle, params });
  const isUsed = isUsedPubState(vehicle.pubStates);
  const isElectric = Object.keys(get(vehicle, 'edmundsTypeCategories', {})).includes('electric');

  const pageDisplayName = isElectric ? ELECTRIC_PAGE_DISPLAY_NAME : PAGE_DISPLAY_NAME;

  return (
    <Container tag="main" id="main-content" className="mpg pt-1 pt-md-1_5">
      <div className="mb-1_5">
        <h1 className="d-inline heading-3">
          {displayVehicle} {pageDisplayName}
        </h1>
        <ScrollLink
          className="text-primary-darker text-underline ms-1"
          to={CHANGE_VEHICLE_ID}
          scrollConfig={{ offset: SCROLL_OFFSET }}
          data-tracking-id="view_content"
        >
          Change vehicle
        </ScrollLink>
      </div>
      <FuelEconomyData
        className="mb-1"
        vehicle={vehicle}
        params={params}
        styles={styles}
        style={currentStyle}
        isUsed={isUsed}
        isMobile={isMobile}
      />

      <Row>
        <Col xs={12} lg={8}>
          <div className="mb-lg-3_5">
            <BuildPriceAd mobile={isMobile} wrapperClass={classnames({ 'd-flex justify-content-center': isMobile })} />
            <hr className="mt-1_5 mb-2" />
          </div>
          <EngineTypeQuiz
            vehicleEngineType={get(currentStyle, 'engineType')}
            vehicleSrpParams={{ make: makeSlug, model: modelSlug }}
          />
          <hr className="mt-1_5 mb-2" />
          <DifferentVehicleMpg />
          <hr className="mt-1_5 mb-2" />
          <NativeAd
            wrapperClass="mrect mx-auto"
            adNameWired="mrect"
            adNameMobile="mmrect"
            nativeStyle="mediumrectangle"
            showLabel
            wiredBreakpoints={{ md: true }}
            dfpCollapse
          />
        </Col>
        <Col xs={12} lg={4} className="pb-2 pb-md-3_5">
          <NativeAd
            wrapperClass="mrect mx-auto"
            adNameWired="mrect"
            nativeStyle="mediumrectangle"
            showLabel
            wiredOnly
            wiredBreakpoints={{ lg: true, xl: true }}
            dfpCollapse
          />
          <hr className="mt-1_5 mb-0 hidden-md-up" />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={8} className="mb-1_5 mb-lg-0">
          <AlsoViewedVehiclesCrossShop modelLinkCode={modelLinkCode} isUsed={isUsed} />
        </Col>
      </Row>

      <MinHeightWrapper componentType="SPONSORED_CARS">
        <SponsoredCars showBottomDividingLine={!isUsed} />
      </MinHeightWrapper>

      {!isUsed && (
        <MinHeightWrapper componentType="SUGGESTED_PRICING_CAROUSEL">
          <PricingCarouselFooter params={params} srpParams={{ ...params, inventorytype: 'new' }} />
        </MinHeightWrapper>
      )}
      <hr className="mt-0 mb-2" />
      <CoreBreadcrumbs
        className="ps-0 mb-2"
        vehicle={vehicle}
        breadcrumbsParams={{ params, pageDisplayName, urlPath: getUrlPathFromParams(params, URL_PATH) }}
      />
      <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} />
    </Container>
  );
}

MpgUI.propTypes = {
  params: MpgParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  styles: PropTypes.arrayOf(MpgEntities.StyleMpg),
  location: PropTypes.shape({}),
  isMobile: PropTypes.bool,
};

MpgUI.defaultProps = {
  vehicle: null,
  styles: null,
  location: null,
  isMobile: false,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  location: get(state, 'pageContext.location'),
});

export const Mpg = connect(mapStateToProps)(
  connectToModel(MpgUI, {
    vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
    styles: bindToPath(({ params }) => buildMpgStylesPath(params), MpgModel),
  })
);
